/**
 * Composable per Workers autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { BaseAutocompleteOptions } from '@/lib/autocompletes';
import type { WorkerAutocompleteResult } from '@/lib/models/worker';
import { getWorkersAutocomplete } from '@/lib/autocompletes/workers';

export function useWorkersAutocomplete(options: BaseAutocompleteComposableOptions = {}) {
	const workers = ref<WorkerAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchWorkers(options: BaseAutocompleteOptions = {}): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getWorkersAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					workers.value = result;
				}
			} catch (err) {
				console.error('❌ ERROR useWorkersAutocomplete.searchWorkers:', err);
				error('Errore durante il caricamento dei lavoratori! Ricaricare la pagina e riprovare.');
				if (token.isLatest()) {
					workers.value = [];
				}
			}
		}, 'searchWorkers');
	}

	function clearWorkers(): void {
		workers.value = [];
	}

	return {
		workers: readonly(workers),
		searchWorkers,
		clearWorkers,
	};
}
